/**
 * GamepadSource — W3C Gamepad API → SDK action map.
 *
 * Polls `navigator.getGamepads()` once per animation frame, aggregates state
 * across every connected pad, and pushes it into the shared `Input` registry
 * under the source name `'gamepad'`. The user's GameScene keeps reading
 * `this.sdk.input.action('fire')` and `this.sdk.input.axis('moveX')` — it
 * never sees a button index.
 *
 * Default mapping matches the W3C "Standard Gamepad" layout (Xbox / PS /
 * generic Bluetooth controllers all report this layout). Pass a custom
 * `mapping` to support arcade sticks, fight sticks, or weird HID layouts.
 *
 * Browser support is broad: Chrome, Firefox, Edge, Safari (desktop), Chrome
 * for Android with USB-OTG / Bluetooth pads, iOS Safari 13+ with MFi/Xbox/
 * PS controllers. Where it doesn't exist (Node tests, ancient browsers),
 * `install()` is a no-op and the SDK falls through to keyboard + touch.
 */

/**
 * Default mapping. `buttons[action]` is an array of standard-layout button
 * indices that count as "pressed = action active". `axes[axisName]` is the
 * single standard-layout axis index that drives that axis.
 */
export const STANDARD_GAMEPAD_MAPPING = Object.freeze({
  buttons: {
    fire:  [0, 7],   // A / Cross + R2
    alt:   [1],      // B / Circle
    up:    [12],     // D-pad up
    down:  [13],     // D-pad down
    left:  [14],     // D-pad left
    right: [15],     // D-pad right
    pause: [9]       // Start
  },
  axes: {
    moveX: 0,        // Left stick X
    moveY: 1         // Left stick Y
  }
});

export class GamepadSource {
  /**
   * @param {object} opts
   * @param {import('./Input.js').Input} opts.input
   * @param {object} [opts.mapping=STANDARD_GAMEPAD_MAPPING]
   * @param {number} [opts.deadzone=0.15]
   */
  constructor(opts) {
    this.input = opts.input;
    this.mapping = opts.mapping || STANDARD_GAMEPAD_MAPPING;
    this.deadzone = opts.deadzone || 0.15;

    this._connected = new Map();
    this._installed = false;
    this._rafId = null;
    this._onConnect = null;
    this._onDisconnect = null;
  }

  /** Start hot-plug listeners and the polling loop. No-op outside browsers. */
  install() {
    if (this._installed) return;
    this._installed = true;
    if (typeof window === 'undefined') return;

    this._onConnect = (ev) => {
      this._connected.set(ev.gamepad.index, ev.gamepad);
    };
    this._onDisconnect = (ev) => {
      this._connected.delete(ev.gamepad.index);
      // If no pads remain, wipe whatever the gamepad source contributed
      // so a stale stick value doesn't keep moving the player.
      if (this._connected.size === 0) {
        this.input.clearSource('gamepad');
      }
    };
    window.addEventListener('gamepadconnected', this._onConnect);
    window.addEventListener('gamepaddisconnected', this._onDisconnect);

    // Some browsers don't fire `gamepadconnected` until the first input.
    // Seed our map from the current state.
    if (typeof navigator !== 'undefined' && navigator.getGamepads) {
      const pads = navigator.getGamepads();
      for (const pad of pads) {
        if (pad && pad.connected) this._connected.set(pad.index, pad);
      }
    }

    if (typeof requestAnimationFrame !== 'undefined') {
      const loop = () => {
        this.poll();
        this._rafId = requestAnimationFrame(loop);
      };
      this._rafId = requestAnimationFrame(loop);
    }
  }

  /** Tear down listeners + RAF loop. */
  destroy() {
    if (!this._installed) return;
    this._installed = false;
    if (typeof window !== 'undefined') {
      if (this._onConnect) window.removeEventListener('gamepadconnected', this._onConnect);
      if (this._onDisconnect) window.removeEventListener('gamepaddisconnected', this._onDisconnect);
    }
    if (this._rafId != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this.input.clearSource('gamepad');
  }

  /**
   * Poll all connected pads and push aggregated state to the Input registry.
   * Called automatically once per RAF in the browser; tests can call it
   * manually after pushing a fake `navigator.getGamepads`.
   */
  poll() {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return;

    const pads = navigator.getGamepads();
    if (!pads || pads.length === 0) {
      this.input.clearSource('gamepad');
      return;
    }

    // Reset the per-frame snapshot we'll OR into.
    /** @type {Object<string, boolean>} */
    const buttonState = {};
    /** @type {Object<string, number>} */
    const axisState = {};

    let anyPad = false;
    for (const pad of pads) {
      if (!pad || !pad.connected) continue;
      anyPad = true;

      // Buttons → actions.
      for (const action of Object.keys(this.mapping.buttons)) {
        if (buttonState[action]) continue;
        const indices = this.mapping.buttons[action];
        for (const idx of indices) {
          const btn = pad.buttons && pad.buttons[idx];
          if (btn && (btn.pressed || (typeof btn === 'number' ? btn > 0.5 : false))) {
            buttonState[action] = true;
            break;
          }
        }
      }

      // Stick axes — pick the largest magnitude across all pads.
      for (const axisName of Object.keys(this.mapping.axes)) {
        const idx = this.mapping.axes[axisName];
        const v = (pad.axes && pad.axes[idx]) || 0;
        if (Math.abs(v) > this.deadzone) {
          if (Math.abs(v) > Math.abs(axisState[axisName] || 0)) {
            axisState[axisName] = v;
          }
        }
      }

      // Treat the analog stick as a synthetic d-pad too, so games that
      // only check `action('left')` etc. work without reading axes.
      const lx = (pad.axes && pad.axes[this.mapping.axes.moveX]) || 0;
      const ly = (pad.axes && pad.axes[this.mapping.axes.moveY]) || 0;
      if (lx < -0.5) buttonState.left = true;
      if (lx > 0.5)  buttonState.right = true;
      if (ly < -0.5) buttonState.up = true;
      if (ly > 0.5)  buttonState.down = true;
    }

    if (!anyPad) {
      this.input.clearSource('gamepad');
      return;
    }

    // Push the snapshot. Always set every action so releasing a button
    // actually clears it (instead of leaving the previous frame's `true`).
    for (const action of Object.keys(this.mapping.buttons)) {
      this.input.setSourceButton('gamepad', action, !!buttonState[action]);
    }
    // The synthetic dpad-from-stick directions need explicit clearing too.
    for (const dir of ['up', 'down', 'left', 'right']) {
      if (this.mapping.buttons[dir] == null) {
        this.input.setSourceButton('gamepad', dir, !!buttonState[dir]);
      }
    }

    for (const axisName of Object.keys(this.mapping.axes)) {
      if (axisState[axisName] != null) {
        this.input.setSourceAxis('gamepad', axisName, axisState[axisName]);
      } else {
        this.input.setSourceAxis('gamepad', axisName, null);
      }
    }
  }

  /** Convenience: count of currently-connected pads. */
  getPadCount() {
    return this._connected.size;
  }
}
